import React, { useRef, useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";

interface TruncatedCellProps {
  content: React.ReactNode;
  className?: string;
}

const TruncatedCell: React.FC<TruncatedCellProps> = ({
  content,
  className
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipContent, setTooltipContent] = useState<React.ReactNode>();

  useEffect(() => {
    const checkTruncation = () => {
      const element = cellRef.current;
      if (!element) return;

      // First check for custom cell with truncate class
      const truncateSpan = element.querySelector("span.truncate");
      if (truncateSpan) {
        const isSpanTruncated =
          truncateSpan.scrollWidth > truncateSpan.clientWidth;
        setIsTruncated(isSpanTruncated);
        setTooltipContent(truncateSpan.textContent);
        return;
      }

      // Fallback for simple content
      const isCellTruncated = element.scrollWidth > element.clientWidth;
      setIsTruncated(isCellTruncated);
      setTooltipContent(element.textContent);
    };

    checkTruncation();
    const resizeObserver = new ResizeObserver(checkTruncation);
    if (cellRef.current) {
      resizeObserver.observe(cellRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [content]);

  if (!isTruncated) {
    return (
      <div ref={cellRef} className={className}>
        {content}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div ref={cellRef} className={className}>
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent maxWidth={18}>{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TruncatedCell;
